import {
  getErrorStatus,
  isServerError,
  isTransientRequestError
} from "~/api/error";

export type TillGate =
  | { screen: "loading" }
  | { screen: "offline" }
  | { screen: "logged-out" }
  | { screen: "load-error" }
  | { screen: "license-missing"; reason: "no-till" | "device-mismatch" }
  | { screen: "app" };

export type TillGateInput = {
  authTillId: string | null;
  isKanplaAdmin: boolean | null;
  deviceId: string | null;
  till: { id: string; deviceId: string | null } | undefined;
  status: "pending" | "error" | "success";
  fetchStatus: "fetching" | "paused" | "idle";
  error: unknown;
  failureReason: unknown;
  isOnline: boolean;
};

export function resolveTillGate({
  authTillId,
  isKanplaAdmin,
  deviceId,
  till,
  status,
  fetchStatus,
  error,
  failureReason,
  isOnline
}: TillGateInput): TillGate {
  // A retried query keeps `error` null and parks the failure in
  // `failureReason`, so reading only `error` misses the whole outage.
  const requestError = error ?? failureReason;

  if (authTillId === null) {
    // A Kanpla admin reaches the app without picking a till, and the query is
    // disabled without one, so the gate must not read that as a missing licence.
    return isKanplaAdmin
      ? { screen: "app" }
      : { screen: "license-missing", reason: "no-till" };
  }

  if (getErrorStatus(requestError) === 404) {
    return { screen: "logged-out" };
  }

  if (status === "pending") {
    if (
      isTransientRequestError(requestError) ||
      !isOnline ||
      fetchStatus === "paused"
    ) {
      return { screen: "offline" };
    }

    return { screen: "loading" };
  }

  if (status === "error") {
    if (isTransientRequestError(requestError)) {
      return { screen: "offline" };
    }

    // A thrown device mismatch never reaches this branch: the query fn logs
    // the till out before throwing, so rule one resolves it as "no-till".
    return { screen: "load-error" };
  }

  // A device that never reported an id cannot be told apart from one still
  // waiting on the native bridge, so it must not be gated.
  if (
    !isKanplaAdmin &&
    typeof deviceId === "string" &&
    till?.deviceId !== deviceId
  ) {
    return { screen: "license-missing", reason: "device-mismatch" };
  }

  return { screen: "app" };
}

type TillGateLog = {
  level: "warn" | "error";
  message: string;
} | null;

export function describeTillGate(gate: TillGate): TillGateLog {
  switch (gate.screen) {
    case "license-missing":
      return gate.reason === "device-mismatch"
        ? { level: "warn", message: "Till device mismatch" }
        : { level: "warn", message: "Till license missing" };
    case "load-error":
      return { level: "error", message: "Till load failed" };
    case "offline":
      return { level: "warn", message: "Till unreachable" };
    default:
      return null;
  }
}

export function tillGateLogProperties(input: TillGateInput) {
  const requestError = input.error ?? input.failureReason;

  return {
    till_id: input.till?.id ?? input.authTillId,
    till_device_id: input.till?.deviceId,
    device_id: input.deviceId,
    device_id_type: typeof input.deviceId,
    auth_till_id: input.authTillId,
    is_kanpla_admin: input.isKanplaAdmin,
    query_status: input.status,
    query_fetch_status: input.fetchStatus,
    failure_name:
      requestError instanceof Error ? requestError.name : typeof requestError,
    failure_text:
      requestError instanceof Error ? requestError.message : undefined,
    failure_status: getErrorStatus(requestError),
    is_transient: isTransientRequestError(requestError),
    is_server_error: isServerError(requestError),
    from_failure_reason:
      (input.error === null || input.error === undefined) &&
      input.failureReason !== null &&
      input.failureReason !== undefined,
    is_online: input.isOnline,
    navigator_online:
      typeof navigator === "undefined" ? undefined : navigator.onLine
  };
}
